import React, { useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';

/**
 * Bulk-import budgets (commercial proposals) from a CSV.
 *
 * The modal owns all import-specific state (CSV text, options, result).
 * Parent only needs to know when a successful import happened so it can
 * refresh its budget list / stats.
 */
export default function ImportBudgetsModal({ open, onOpenChange, onImported }) {
  const [csvText, setCsvText] = useState('');
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCsvText(ev.target.result || '');
    reader.readAsText(file, 'utf-8');
  };

  const handleImport = async () => {
    if (!csvText.trim()) {
      toast.error('Cole o conteúdo do CSV ou selecione um arquivo');
      return;
    }
    setImporting(true);
    setResult(null);
    try {
      const res = await api.post('/budgets/bulk-import', {
        csv: csvText,
        skip_duplicates: skipDuplicates,
      });
      setResult(res.data);
      toast.success(
        `Importação concluída: ${res.data.imported} importadas, ${res.data.skipped} puladas, ${res.data.errors} erros`
      );
      await onImported?.();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Erro';
      toast.error(`Falha ao importar: ${detail}`);
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setCsvText('');
    setResult(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Importar Propostas via CSV</DialogTitle>
          <DialogDescription>
            Útil para cadastrar propostas históricas de uma só vez. Colunas
            esperadas (cabeçalho): Número da Proposta, Cliente, Projeto,
            Localização, Data da Proposta (DD/MM/AAAA), Escopo Resumido,
            Valor Total (R$), Condições de Pagamento, Validade da Proposta,
            Aprovação (Sim/Não/Não consta).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <Label htmlFor="csv-file">1) Selecione o arquivo CSV</Label>
            <Input
              id="csv-file"
              type="file"
              accept=".csv,text/csv"
              onChange={handleFileChange}
              data-testid="input-import-csv-file"
              className="cursor-pointer"
            />
            <p className="text-xs text-gray-500 mt-1">Ou cole o conteúdo abaixo:</p>
          </div>

          <div>
            <Label htmlFor="csv-text">2) Conteúdo CSV</Label>
            <Textarea
              id="csv-text"
              rows={8}
              placeholder="Número da Proposta,Cliente,Projeto,..."
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              className="font-mono text-xs"
              data-testid="textarea-import-csv"
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={skipDuplicates}
              onChange={(e) => setSkipDuplicates(e.target.checked)}
              data-testid="checkbox-skip-duplicates"
            />
            Pular duplicados (mesmo número de proposta)
          </label>

          {result && (
            <div className="border rounded-lg p-4 bg-gray-50 space-y-2 max-h-72 overflow-y-auto">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-2xl font-bold text-emerald-700">{result.imported}</p>
                  <p className="text-xs text-gray-600">Importadas</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600">{result.skipped}</p>
                  <p className="text-xs text-gray-600">Puladas</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-600">{result.errors}</p>
                  <p className="text-xs text-gray-600">Erros</p>
                </div>
              </div>

              {result.details?.errors?.length > 0 && (
                <div className="text-xs">
                  <p className="font-semibold text-red-700 mt-2">Erros:</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    {result.details.errors.slice(0, 10).map((er, i) => (
                      <li key={`err-${i}`}>
                        Linha {er.row} ({er.proposal_number || '-'}): {er.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {result.details?.skipped?.length > 0 && (
                <div className="text-xs">
                  <p className="font-semibold text-amber-700 mt-2">Puladas:</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    {result.details.skipped.slice(0, 10).map((sk, i) => (
                      <li key={`sk-${i}`}>
                        Linha {sk.row} ({sk.proposal_number}): {sk.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={handleClose}>Fechar</Button>
            <Button
              onClick={handleImport}
              disabled={importing || !csvText.trim()}
              className="bg-blue-700 hover:bg-blue-800"
              data-testid="btn-confirm-import"
            >
              {importing ? 'Importando...' : 'Importar'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
